
import React, { useState, useEffect } from 'react';
import {
  X,
  RefreshCw,
  Clock,
  Calendar,
  AlertCircle,
  Activity,
  BarChart3,
  FileText,
  Users,
  MessageSquare,
  Building,
  Building2,
  Download
} from 'lucide-react';

import { api } from '../services/api';
import { AdminLoading } from './admin/AdminLoading';
import { AdminPageHeader } from './admin/AdminPageHeader';
import AppHeader from './AppHeader';

const Monitoring = ({ user, onLogout }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [filter, setFilter] = useState('ALL');
  const [activeTab, setActiveTab] = useState('reporting');

  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsStoreFilter, setStatsStoreFilter] = useState(null);
  const [stores, setStores] = useState([]);

  const [reportingSubTab, setReportingSubTab] = useState('sms-button');

  const [smsReportStores, setSmsReportStores] = useState([]);
  const [selectedSmsReportStore, setSelectedSmsReportStore] = useState(null);
  const [smsReportLoading, setSmsReportLoading] = useState(false);
  const [smsReportDownloading, setSmsReportDownloading] = useState(false);
  const [smsReportViewMode, setSmsReportViewMode] = useState('store');
  const [smsReportData, setSmsReportData] = useState([]);
  const [smsAllStoresReport, setSmsAllStoresReport] = useState([]);
  const [smsExpandedStores, setSmsExpandedStores] = useState(new Set());

  useEffect(() => {
    const loadInitialData = async () => {
      await Promise.all([
        loadTasks(),
        loadStores(),
        loadSmsReportStores()
      ]);
    };

    loadInitialData();
  }, []);

  useEffect(() => {
    loadStats();
  }, [statsStoreFilter]);

  useEffect(() => {
    if (selectedSmsReportStore && smsReportViewMode === 'store') {
      loadSmsStoreReport(selectedSmsReportStore.id);
    }
  }, [selectedSmsReportStore, smsReportViewMode]);

  const loadTasks = async () => {
    setError('');

    try {
      setLoading(true);

      const response = await api.get('/admin/scheduled-tasks');

      setTasks(response.data);
    } catch (error) {
      console.error('Error loading scheduled tasks:', error);
      setError('Failed to load scheduled tasks');
    } finally {
      setLoading(false);
    }
  };

  const cancelTask = async (task) => {
    if (!window.confirm('Are you sure you want to cancel this scheduled task?')) {
      return;
    }

    try {
      if (task.jobGroup === 'log-mode-jobs' && task.messageId) {
        // Use the log-mode endpoint so the message is marked INVALIDATED,
        // not just the Quartz job deleted.
        await api.post(`/messages/${task.messageId}/log-mode/cancel`);
      } else {
        await api.post(
          `/admin/scheduled-tasks/${task.jobKey}/cancel`,
          {
            jobGroup: task.jobGroup || 'cancel-appointment-jobs'
          }
        );
      }

      await loadTasks();
    } catch (error) {
      console.error('Error cancelling task:', error);
      alert('Failed to cancel task');
    }
  };

  const getFilteredTasks = () => {
    if (filter === 'ALL') {
      return tasks;
    }

    return tasks.filter((task) => task.status === filter);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'WAITING':
        return <Clock className="w-4 h-4 text-yellow-500" />;

      case 'PAUSED':
        return <Clock className="w-4 h-4 text-orange-500" />;

      case 'ACQUIRED':
        return <Clock className="w-4 h-4 text-blue-500" />;

      case 'BLOCKED':
      case 'ERROR':
        return <AlertCircle className="w-4 h-4 text-red-500" />;

      default:
        return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'WAITING':
        return 'bg-yellow-100 text-yellow-800';

      case 'PAUSED':
        return 'bg-orange-100 text-orange-800';

      case 'ACQUIRED':
        return 'bg-blue-100 text-blue-800';

      case 'BLOCKED':
      case 'ERROR':
        return 'bg-red-100 text-red-800';

      case 'NEW':
        return 'bg-blue-100 text-blue-800';

      case 'IN_PROGRESS':
        return 'bg-yellow-100 text-yellow-800';

      case 'BOOKED':
        return 'bg-green-100 text-green-800';

      case 'RESCHEDULED':
        return 'bg-purple-100 text-purple-800';

      case 'CANCELLED':
        return 'bg-red-100 text-red-800';

      case 'COMPLETED':
        return 'bg-gray-100 text-gray-800';

      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getAppointmentStatusColor = (status) => {
    switch (status) {
      case 'PENDING':
        return 'bg-yellow-500';

      case 'BOOKED':
        return 'bg-green-500';

      case 'RESCHEDULED':
        return 'bg-orange-500';

      case 'CANCELED':
      case 'CANCELLED':
        return 'bg-red-500';

      default:
        return 'bg-gray-500';
    }
  };

  const formatDateTime = (dateTime) => {
    if (!dateTime) {
      return '-';
    }

    return new Date(dateTime).toLocaleString();
  };

  const formatTaskType = (jobGroup) => {
    return jobGroup === 'log-mode-jobs' ? 'Log-Mode Message' : 'Cancel Appointment';
  };

  const loadStats = async () => {
    try {
      setStatsLoading(true);

      const url = statsStoreFilter
        ? `/admin/statistics?storeId=${statsStoreFilter}`
        : '/admin/statistics';

      const response = await api.get(url);

      setStats(response.data);
    } catch (error) {
      console.error('Error loading statistics:', error);

      // Fallback data if API fails.
      setStats({
        totalLeads: 1250,
        totalMessages: 8432,

        leadsByStatus: {
          NEW: 156,
          IN_PROGRESS: 234,
          BOOKED: 456,
          RESCHEDULED: 89,
          CANCELLED: 67,
          COMPLETED: 248
        },

        appointmentsByStatus: {
          PENDING: 0,
          BOOKED: 0,
          RESCHEDULED: 0,
          CANCELED: 0
        },

        messagesByType: {
          INBOUND: 4521,
          OUTBOUND: 3911,
          HANDOFF: 0,
          HIDDEN: 0
        },

        activeStores: 23,
        totalEnterprises: 5
      });
    } finally {
      setStatsLoading(false);
    }
  };

  const loadStores = async () => {
    try {
      const response = await api.get('/admin/stores');

      setStores(response.data);
    } catch (error) {
      console.error('Error loading stores:', error);
    }
  };

  const loadSmsReportStores = async () => {
    try {
      setSmsReportLoading(true);

      const response = await api.get('/admin/stores');

      setSmsReportStores(response.data);

      if (response.data.length > 0) {
        setSelectedSmsReportStore(response.data[0]);
      }
    } catch (error) {
      console.error('Error loading SMS report stores:', error);
    } finally {
      setSmsReportLoading(false);
    }
  };

  const loadSmsStoreReport = async (storeId) => {
    try {
      setSmsReportLoading(true);

      const response = await api.get(
        `/admin/sms-button-reporting/store/${storeId}`
      );

      setSmsReportData(response.data);
    } catch (error) {
      console.error('Error loading SMS store report:', error);
      setSmsReportData([]);
    } finally {
      setSmsReportLoading(false);
    }
  };

  const loadSmsAllStoresReport = async () => {
    try {
      setSmsReportLoading(true);

      const response = await api.get(
        '/admin/sms-button-reporting/all-stores'
      );

      setSmsAllStoresReport(response.data);
    } catch (error) {
      console.error('Error loading SMS all stores report:', error);
      setSmsAllStoresReport([]);
    } finally {
      setSmsReportLoading(false);
    }
  };

  const handleSmsReportViewModeChange = (mode) => {
    setSmsReportViewMode(mode);

    if (mode === 'all') {
      loadSmsAllStoresReport();
    }
  };

  const toggleSmsStoreExpansion = (storeId) => {
    setSmsExpandedStores((previous) => {
      const next = new Set(previous);

      if (next.has(storeId)) {
        next.delete(storeId);
      } else {
        next.add(storeId);
      }

      return next;
    });
  };

  const handleSmsReportDownload = async () => {
    if (!selectedSmsReportStore) {
      return;
    }

    setSmsReportDownloading(true);

    try {
      const response = await api.get(
        `/admin/sms-button-reporting/store/${selectedSmsReportStore.id}/export`,
        {
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(
        new Blob([response.data])
      );

      const link = document.createElement('a');

      link.href = url;

      const storeName =
        selectedSmsReportStore.name ||
        `Store_${selectedSmsReportStore.id}`;

      const safeStoreName = storeName.replace(
        /[^a-zA-Z0-9_-]/g,
        '_'
      );

      link.setAttribute(
        'download',
        `sms_button_report_${safeStoreName}.xlsx`
      );

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading SMS report:', error);
      alert('Failed to download report');
    } finally {
      setSmsReportDownloading(false);
    }
  };

  const handleSmsReportDownloadAll = async () => {
    setSmsReportDownloading(true);

    try {
      const response = await api.get(
        '/admin/sms-button-reporting/all-stores/export',
        {
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(
        new Blob([response.data])
      );

      const link = document.createElement('a');

      link.href = url;
      link.setAttribute(
        'download',
        'sms_button_report_all_stores.xlsx'
      );

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(
        'Error downloading SMS all stores report:',
        error
      );

      alert('Failed to download report');
    } finally {
      setSmsReportDownloading(false);
    }
  };

  return (
    <div className="flex flex-col h-screen bg-gray-50 overflow-hidden">
      <AppHeader user={user} onLogout={onLogout} />

      <div className="flex-1 overflow-y-auto py-8">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="bg-white shadow rounded-lg w-full min-h-[calc(100vh-4rem)]">
            <div className="px-4 py-5 sm:p-6">

              {loading ? (
                <AdminLoading message="Loading monitoring data..." />
              ) : (
              <>

              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <Activity className="h-8 w-8 text-gray-600 mr-3" />
                  <h1 className="text-2xl font-bold text-gray-900">
                    Monitoring
                  </h1>
                </div>
              </div>

              {/* Main Tabs */}
              <div className="mb-6">
                <div className="flex space-x-1 border-b border-gray-200">

                  <button
                    onClick={() => setActiveTab('reporting')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 flex items-center ${
                      activeTab === 'reporting'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Reporting
                  </button>

                  <button
                    onClick={() => setActiveTab('scheduled-tasks')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 flex items-center ${
                      activeTab === 'scheduled-tasks'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Clock className="h-4 w-4 mr-2" />
                    Scheduled Tasks
                  </button>

                  <button
                    onClick={() => setActiveTab('statistics')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 flex items-center ${
                      activeTab === 'statistics'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <BarChart3 className="h-4 w-4 mr-2" />
                    Statistics
                  </button>

                </div>
              </div>

              {/* Error */}
              {error && (
                <div className="rounded-md bg-red-50 p-4 mb-6">
                  <div className="flex">
                    <AlertCircle className="h-5 w-5 text-red-400" />

                    <div className="ml-3">
                      <p className="text-sm text-red-800">
                        {error}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* ============================================================
                  REPORTING
                  ============================================================ */}
              {activeTab === 'reporting' && (
                <div>
                  <AdminPageHeader
                    icon={FileText}
                    title="Reporting"
                    subtitle="View and generate reports"
                  />

                  <div className="mb-6">
                    <div className="flex space-x-1 border-b border-gray-200">

                      <button
                        onClick={() =>
                          setReportingSubTab('sms-button')
                        }
                        className={`px-4 py-2 text-sm font-medium border-b-2 flex items-center ${
                          reportingSubTab === 'sms-button'
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <MessageSquare className="h-4 w-4 mr-2" />
                        SMS Button Reports
                      </button>

                    </div>
                  </div>

                  {reportingSubTab === 'sms-button' && (
                    <div className="bg-white rounded-lg shadow p-6">

                      {/* Report Mode */}
                      <div className="flex items-center space-x-3 mb-6">

                        <button
                          onClick={() =>
                            handleSmsReportViewModeChange('store')
                          }
                          className={`px-4 py-2 rounded-md text-sm font-medium ${
                            smsReportViewMode === 'store'
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          By Store
                        </button>

                        <button
                          onClick={() =>
                            handleSmsReportViewModeChange('all')
                          }
                          className={`px-4 py-2 rounded-md text-sm font-medium ${
                            smsReportViewMode === 'all'
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          All Stores
                        </button>

                      </div>

                      {/* By Store */}
                      {smsReportViewMode === 'store' && (
                        <div className="space-y-4">

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Select Store
                            </label>

                            <select
                              value={
                                selectedSmsReportStore?.id || ''
                              }
                              onChange={(e) => {
                                const store =
                                  smsReportStores.find(
                                    (s) =>
                                      s.id ===
                                      parseInt(e.target.value, 10)
                                  );

                                setSelectedSmsReportStore(store);
                              }}
                              className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                              disabled={smsReportLoading}
                            >
                              {smsReportStores.map((store) => (
                                <option
                                  key={store.id}
                                  value={store.id}
                                >
                                  {store.name || store.storeId}
                                </option>
                              ))}
                            </select>
                          </div>

                          {smsReportLoading ? (
                            <div className="text-center py-8">
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto" />
                              <span className="ml-3 text-gray-600">
                                Loading report...
                              </span>
                            </div>
                          ) : smsReportData.length === 0 ? (
                            <div className="text-center py-8 text-gray-500">
                              No SMS button data available for this store
                            </div>
                          ) : (
                            <div className="bg-white shadow rounded-lg overflow-hidden">
                              <table className="min-w-full divide-y divide-gray-200">

                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Button Text
                                    </th>

                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Button Body Text
                                    </th>

                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Lead Count
                                    </th>
                                  </tr>
                                </thead>

                                <tbody className="bg-white divide-y divide-gray-200">
                                  {smsReportData.map(
                                    (item, index) => (
                                      <tr key={index}>

                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                          {item.buttonText}
                                        </td>

                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                          {item.buttonBodyText}
                                        </td>

                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                          {item.leadCount}
                                        </td>

                                      </tr>
                                    )
                                  )}
                                </tbody>

                              </table>
                            </div>
                          )}

                          <button
                            onClick={handleSmsReportDownload}
                            disabled={
                              !selectedSmsReportStore ||
                              smsReportDownloading ||
                              smsReportData.length === 0
                            }
                            className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                          >
                            <Download className="h-4 w-4 mr-2" />

                            {smsReportDownloading
                              ? 'Downloading...'
                              : 'Download Excel Report'}
                          </button>

                        </div>
                      )}

                      {/* All Stores */}
                      {smsReportViewMode === 'all' && (
                        <div>

                          {smsReportLoading ? (
                            <div className="text-center py-8">
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto" />

                              <span className="ml-3 text-gray-600">
                                Loading report...
                              </span>
                            </div>
                          ) : smsAllStoresReport.length === 0 ? (
                            <div className="text-center py-8 text-gray-500">
                              No SMS button data available
                            </div>
                          ) : (
                            <div className="space-y-4">

                              {smsAllStoresReport.map(
                                (storeReport) => (
                                  <div
                                    key={storeReport.storeId}
                                    className="bg-white shadow rounded-lg overflow-hidden"
                                  >

                                    <button
                                      onClick={() =>
                                        toggleSmsStoreExpansion(
                                          storeReport.storeId
                                        )
                                      }
                                      className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                                    >
                                      <div className="flex items-center space-x-3">

                                        <span className="text-sm font-medium text-gray-900">
                                          {storeReport.storeName ||
                                            `Store ${storeReport.storeId}`}
                                        </span>

                                        <span className="text-xs text-gray-500">
                                          (
                                          {storeReport.buttons?.length ||
                                            0}{' '}
                                          buttons)
                                        </span>

                                      </div>

                                      <svg
                                        className={`h-5 w-5 text-gray-400 transition-transform ${
                                          smsExpandedStores.has(
                                            storeReport.storeId
                                          )
                                            ? 'rotate-180'
                                            : ''
                                        }`}
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M19 9l-7 7-7-7"
                                        />
                                      </svg>
                                    </button>

                                    {smsExpandedStores.has(
                                      storeReport.storeId
                                    ) && (
                                      <div className="border-t border-gray-200">

                                        <table className="min-w-full divide-y divide-gray-200">

                                          <thead className="bg-gray-50">
                                            <tr>

                                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Button Text
                                              </th>

                                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Button Body Text
                                              </th>

                                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Lead Count
                                              </th>

                                            </tr>
                                          </thead>

                                          <tbody className="bg-white divide-y divide-gray-200">
                                            {(storeReport.buttons || []).map(
                                              (button, index) => (
                                                <tr key={index}>

                                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                    {button.buttonText}
                                                  </td>

                                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {button.buttonBodyText}
                                                  </td>

                                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {button.leadCount}
                                                  </td>

                                                </tr>
                                              )
                                            )}
                                          </tbody>

                                        </table>

                                      </div>
                                    )}

                                  </div>
                                )
                              )}

                            </div>
                          )}

                          <button
                            onClick={handleSmsReportDownloadAll}
                            disabled={
                              smsReportDownloading ||
                              smsAllStoresReport.length === 0
                            }
                            className="w-full mt-4 inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                          >
                            <Download className="h-4 w-4 mr-2" />

                            {smsReportDownloading
                              ? 'Downloading...'
                              : 'Download Excel Report (All Stores)'}
                          </button>

                        </div>
                      )}

                    </div>
                  )}
                </div>
              )}

              {/* ============================================================
                  SCHEDULED TASKS
                  ============================================================ */}
              {activeTab === 'scheduled-tasks' && (
                <div>
                  <AdminPageHeader
                    icon={Clock}
                    title="Scheduled Tasks"
                    subtitle="View and manage scheduled background tasks"
                  />

                  <div className="bg-white rounded-lg shadow">

                    {/* Header */}
                    <div className="p-4 border-b flex items-center justify-between">

                      <div className="flex items-center gap-4">

                        <select
                          value={filter}
                          onChange={(e) =>
                            setFilter(e.target.value)
                          }
                          className="px-3 py-2 border rounded-lg text-sm"
                        >
                          <option value="ALL">
                            All Status
                          </option>

                          <option value="WAITING">
                            Waiting
                          </option>

                          <option value="PAUSED">
                            Paused
                          </option>

                          <option value="ACQUIRED">
                            Acquired
                          </option>

                          <option value="BLOCKED">
                            Blocked
                          </option>

                          <option value="ERROR">
                            Error
                          </option>
                        </select>

                        <span className="text-sm text-gray-500">
                          {getFilteredTasks().length} tasks
                        </span>

                      </div>

                      <button
                        onClick={loadTasks}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
                      >
                        <RefreshCw className="w-4 h-4" />
                        Refresh
                      </button>

                    </div>

                    {/* Tasks table */}
                    <div className="overflow-x-auto">

                      <table className="w-full">

                        <thead className="bg-gray-50">
                          <tr>

                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Job Key
                            </th>

                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Type
                            </th>

                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Status
                            </th>

                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Lead ID
                            </th>

                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Appointment ID
                            </th>

                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Scheduled Time
                            </th>

                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Actions
                            </th>

                          </tr>
                        </thead>

                        <tbody className="divide-y">

                          {getFilteredTasks().length === 0 ? (
                            <tr>
                              <td
                                colSpan="7"
                                className="px-4 py-8 text-center text-gray-500"
                              >
                                No scheduled tasks found
                              </td>
                            </tr>
                          ) : (
                            getFilteredTasks().map((task) => (
                              <tr
                                key={task.jobKey}
                                className="hover:bg-gray-50"
                              >

                                <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                  {task.jobKey}
                                </td>

                                <td className="px-4 py-3 text-sm text-gray-700">
                                  {formatTaskType(task.jobGroup)}
                                </td>

                                <td className="px-4 py-3">
                                  <span
                                    className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                                      task.status
                                    )}`}
                                  >
                                    {getStatusIcon(task.status)}
                                    {task.status}
                                  </span>
                                </td>

                                <td className="px-4 py-3 text-sm text-gray-700">
                                  {task.leadId || '-'}
                                </td>

                                <td className="px-4 py-3 text-sm text-gray-700">
                                  {task.appointmentId || '-'}
                                </td>

                                <td className="px-4 py-3 text-sm text-gray-700">
                                  <div className="flex items-center gap-1">
                                    <Calendar className="w-3 h-3 text-gray-400" />
                                    {formatDateTime(
                                      task.scheduledTime
                                    )}
                                  </div>
                                </td>

                                <td className="px-4 py-3">
                                  <button
                                    onClick={() =>
                                      cancelTask(task)
                                    }
                                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition"
                                  >
                                    <X className="w-4 h-4" />
                                    Unschedule
                                  </button>
                                </td>

                              </tr>
                            ))
                          )}

                        </tbody>

                      </table>

                    </div>
                  </div>
                </div>
              )}

              {/* ============================================================
                  STATISTICS
                  ============================================================ */}
              {activeTab === 'statistics' && (
                <div>
                  <AdminPageHeader
                    icon={BarChart3}
                    title="Statistics"
                    subtitle="View lead and message statistics"
                  />

                  {/* Store Filter */}
                  <div className="mb-6">

                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Filter by Store
                    </label>

                    <select
                      value={statsStoreFilter || ''}
                      onChange={(e) =>
                        setStatsStoreFilter(
                          e.target.value
                            ? Number(e.target.value)
                            : null
                        )
                      }
                      className="block w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">
                        All Stores
                      </option>

                      {stores.map((store) => (
                        <option
                          key={store.id}
                          value={store.id}
                        >
                          {store.name || store.storeId}
                        </option>
                      ))}
                    </select>

                  </div>

                  {statsLoading ? (
                    <div className="flex items-center justify-center py-16">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />

                      <span className="ml-3 text-gray-600">
                        Loading statistics...
                      </span>
                    </div>
                  ) : stats ? (
                    <div className="space-y-6 mt-6">

                      {/* Overview Cards */}
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">

                        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-6 text-white">
                          <div className="flex items-center justify-between">

                            <div>
                              <p className="text-blue-100 text-sm font-medium">
                                Total Leads
                              </p>

                              <p className="text-3xl font-bold mt-2">
                                {stats.totalLeads?.toLocaleString() || 0}
                              </p>
                            </div>

                            <div className="bg-white/20 rounded-full p-3">
                              <Users className="h-6 w-6" />
                            </div>

                          </div>
                        </div>

                        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-6 text-white">
                          <div className="flex items-center justify-between">

                            <div>
                              <p className="text-green-100 text-sm font-medium">
                                Total Messages
                              </p>

                              <p className="text-3xl font-bold mt-2">
                                {stats.totalMessages?.toLocaleString() || 0}
                              </p>
                            </div>

                            <div className="bg-white/20 rounded-full p-3">
                              <MessageSquare className="h-6 w-6" />
                            </div>

                          </div>
                        </div>

                        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-6 text-white">
                          <div className="flex items-center justify-between">

                            <div>
                              <p className="text-purple-100 text-sm font-medium">
                                Active Stores
                              </p>

                              <p className="text-3xl font-bold mt-2">
                                {stats.activeStores || 0}
                              </p>
                            </div>

                            <div className="bg-white/20 rounded-full p-3">
                              <Building className="h-6 w-6" />
                            </div>

                          </div>
                        </div>

                        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg p-6 text-white">
                          <div className="flex items-center justify-between">

                            <div>
                              <p className="text-orange-100 text-sm font-medium">
                                Enterprises
                              </p>

                              <p className="text-3xl font-bold mt-2">
                                {stats.totalEnterprises || 0}
                              </p>
                            </div>

                            <div className="bg-white/20 rounded-full p-3">
                              <Building2 className="h-6 w-6" />
                            </div>

                          </div>
                        </div>

                      </div>

                      {/* Appointments by Status */}
                      <div className="bg-white rounded-lg shadow p-6">

                        <h3 className="text-lg font-semibold text-gray-900 mb-4">
                          Appointments by Status
                        </h3>

                        <div className="space-y-4">

                          {Object.entries(
                            stats.appointmentsByStatus || {}
                          ).map(([status, count]) => {

                            const values = Object.values(
                              stats.appointmentsByStatus || {}
                            );

                            const maxCount = Math.max(
                              ...values,
                              1
                            );

                            const percentage =
                              (Number(count) / maxCount) * 100;

                            const statusColor =
                              getAppointmentStatusColor(status);

                            return (
                              <div key={status}>

                                <div className="flex items-center justify-between mb-1">

                                  <div className="flex items-center gap-2">

                                    <div
                                      className={`w-3 h-3 rounded-full ${statusColor}`}
                                    />

                                    <span className="text-sm font-medium text-gray-700">
                                      {status}
                                    </span>

                                  </div>

                                  <span className="text-sm font-semibold text-gray-900">
                                    {count}
                                  </span>

                                </div>

                                <div className="w-full bg-gray-200 rounded-full h-2.5">

                                  <div
                                    className={`h-2.5 rounded-full ${statusColor}`}
                                    style={{
                                      width: `${percentage}%`
                                    }}
                                  />

                                </div>

                              </div>
                            );
                          })}

                        </div>

                      </div>

                      {/* Messages by Type */}
                      <div className="bg-white rounded-lg shadow p-6">

                        <h3 className="text-lg font-semibold text-gray-900 mb-4">
                          Messages by Type
                        </h3>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">

                          <div className="bg-blue-50 rounded-lg p-4">
                            <p className="text-sm text-blue-600 font-medium">
                              Inbound
                            </p>

                            <p className="text-2xl font-bold text-blue-900 mt-1">
                              {stats.messagesByType?.INBOUND?.toLocaleString() || 0}
                            </p>
                          </div>

                          <div className="bg-green-50 rounded-lg p-4">
                            <p className="text-sm text-green-600 font-medium">
                              Outbound
                            </p>

                            <p className="text-2xl font-bold text-green-900 mt-1">
                              {stats.messagesByType?.OUTBOUND?.toLocaleString() || 0}
                            </p>
                          </div>

                          <div className="bg-purple-50 rounded-lg p-4">
                            <p className="text-sm text-purple-600 font-medium">
                              Handoff
                            </p>

                            <p className="text-2xl font-bold text-purple-900 mt-1">
                              {stats.messagesByType?.HANDOFF?.toLocaleString() || 0}
                            </p>
                          </div>

                          <div className="bg-gray-50 rounded-lg p-4">
                            <p className="text-sm text-gray-600 font-medium">
                              Hidden
                            </p>

                            <p className="text-2xl font-bold text-gray-900 mt-1">
                              {stats.messagesByType?.HIDDEN?.toLocaleString() || 0}
                            </p>
                          </div>

                        </div>

                      </div>

                    </div>
                  ) : (
                    <div className="bg-white rounded-lg shadow p-6">
                      <p className="text-gray-600">
                        No statistics available
                      </p>
                    </div>
                  )}

                </div>
              )}

              </>
              )}

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Monitoring;
